import { getSeriesWindowing } from "../utils/dicomVoi";
import { DEFAULT_WINDOWING } from "../constants/windowPresets";
// src/components/CaseViewer.tsx

import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import type { Editor } from "@tiptap/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { FaTimes } from "../utils/icons";
import { useAuth } from "../context/AuthContext";
import PrototypeReport from "./PrototypeReport";
import { NeuroReviewPanel } from "@/components/neuro/NeuroReviewPanel";
import {
  TriageCollapsedRail,
  TriageNotificationPanel,
  TriageNotRoutedPanel,
  triageRailCount,
} from "@/components/triage/TriageNotificationPanel";
import { isNeuroModel } from "@/types/neuroReview";
import { neuroFindingTargets } from "@/utils/neuroFindingTargets";
import { useWorker } from "../context/WorkerContext";
import { LeftSidebar, WindowPreset, CollapsedSidebarStrip } from "./sidebar";
import {
  SegmentationAnnotationPanel,
  type PredictionEditActions,
} from "./labeling/SegmentationAnnotationPanel";
import { SegmentationStudyFooter } from "./labeling/SegmentationStudyFooter";
import { StoneOverlayLoader } from "./StoneOverlayLoader";
import { getStoneOverlayProjectId } from "../config/stoneOverlayDemo";
import {
  useAnnotationsQuery,
  savedAnnotationsBlocked,
  useUpdateAnnotationMutation,
  type AnnotationInput,
  type AnnotationRecordResponse,
} from "../hooks/useLabelingQueries";
import ComparisonPanel from "./reporter/ComparisonPanel";
import SurfacerPanel from "./surfacer/SurfacerPanel";
import OriginalReportPanel from "./surfacer/OriginalReportPanel";
import { ReviewerReportShell } from "./reviewer/ReviewerReportShell";
import { useReporterComparison } from "../hooks/useReporterComparisonQueries";
import { useFindingSeriesPreload } from "../hooks/useFindingSeriesPreload";
import { useSurfacerCandidates } from "../hooks/useSurfacerCandidates";
import { useViewerHotkeys } from "../hooks/useViewerHotkeys";
import {
  leftSidebarCollapsedAtom,
  rightSidebarCollapsedAtom,
  rightSidebarWidthAtom,
  triagePanelWidthAtom,
  RIGHT_SIDEBAR_MIN_WIDTH,
  RIGHT_SIDEBAR_MAX_WIDTH,
  surfacerSplitAtom,
  SURFACER_SPLIT_MIN,
  SURFACER_SPLIT_MAX,
  focusModeAtom,
} from "../atoms/uiAtoms";
import {
  measureModeAtom,
  measureLoadingAtom,
  measureErrorAtom,
  measureSessionAtom,
  measureClickPinAtom,
} from "../atoms/measureAtoms";
import { hoveredSurfaceAtom } from "../atoms/hoveredSurfaceAtom";
import type { Surface } from "../constants/viewerActions";
import { useCaseViewerSurfaces } from "../hooks/useViewerSurfaces";
import { DEFAULT_CASE_VIEWER_SURFACES } from "../utils/viewerSurfaces";
import { caseViewerGridLayout } from "./caseViewerGridLayout";
import { useDicomSeriesLoader } from "../hooks/useDicomSeriesLoader";
import VolumeViewport from "./VolumeViewport";
import StackViewport from "./StackViewport";
import { ViewportModeBadge } from "./viewport/ViewportModeBadge";
import { decideViewport } from "../utils/pickViewportComponent";
import type { CaseLockReason } from "../utils/caseStatus";
import { HangingProtocolGrid } from "./hanging-protocol";
import { hasHangingProtocolSupport } from "../utils/hangingProtocolService";
import { shouldAutoInvert } from "../utils/dicomMetadataLoader";
import { warnOnMixedSignedTransferSyntax } from "../utils/signCorrectionTelemetry";
import { SeriesPicker } from "./common";
import { GpuVerdictBanner } from "./common/GpuVerdictBanner";
import { getBaseFilename } from "../utils/fileHelpers";
import { useModelPrediction, type ModelPredictionResponse } from "../hooks/useStudyQueries";
import { useAnnotationSelectionMirror } from "../hooks/useAnnotationSelectionMirror";
import { useReport, type FindingJumpTarget } from "../hooks/useReportQuery";
import { runCommand } from "../lib/commands";
import { emit } from "@/lib/viewerBus";
import { focusedFindingIndexAtom, orderedFindingsAtom } from "../atoms/findingsAtoms";
import { LoadingOverlay } from "./case-viewer/LoadingOverlay";
import { StudyNotCanonicalizedPanel } from "./case-viewer/StudyNotCanonicalizedPanel";
import {
  currentSliceAtom,
  instanceSliceInfoMapAtom,
  loadedSeriesAtom,
  totalSlicesAtom,
} from "../atoms/selectionAtoms";
import {
  allSlotSeriesSlicesSelector,
  hangingProtocolActionsAtom,
  activeSlotAtom,
} from "../atoms/hangingProtocolAtoms";
import { resetCaseAtomsAtom } from "../atoms/caseResetAtom";
import { gpuVerdictAtom } from "../atoms/gpuVerdictAtom";
import {
  getCornerstone,
  getCornerstoneTools,
  purgeAllCornerstoneCache,
} from "../utils/dicomLoader";
import { companyIdFromToken, shouldShowReviewerPanel } from "../utils/reporterViewGate";
import { StoneRail } from "./stones/StoneRail";
import { SegmentationPreviewRail } from "./predictions/SegmentationPreviewRail";
import type { EditHiddenReason } from "./predictions/SegmentationPreviewRail";
import { CandidateDecisionStatus } from "@/components/predictions/CandidateDecisionStatus";
import { useStoneFindingsQuery } from "../hooks/useStoneFindingsQuery";
import { useStoneReportSync } from "../hooks/useStoneReportSync";
import { useStoneWorklistNav } from "../hooks/useStoneWorklistNav";
import { stoneVerdictsAtom, stoneAttrEditsAtom } from "../atoms/stoneReviewAtoms";
import { emitStoneEvent, buildStoneEvent } from "../stones/stoneTelemetry";
import { loadS3PreviewFrame } from "../utils/dicomPreviewFrame";
import { previewWindowPixels } from "@/utils/previewWindowPixels";
import { segmentationMarksAtom } from "../atoms/segmentationMarksAtom";
import { resolveKeySliceMarks, resolveSegmentationMarks } from "../predictions/segmentationMarks";
import {
  companionMasksBlockedAtom,
  nnInteractiveBlockedAtom,
  segmentationVolumeIdAtom,
} from "../atoms/segmentationAtoms";
import { getSegmentationCardAnnotations } from "@/predictions/savedSegmentationCards";
import { useSavedCardGeometry } from "@/hooks/useSavedCardGeometry";
import { WorkListButton } from "./labeling/WorkListButton";
import { SegmentationCaseTimer } from "./study/SegmentationCaseTimer";
import {
  predictionMaskSegmentToLabelmap,
  resolvePredictionMask,
} from "../predictions/segmentationMask";
import {
  buildSegmentationPreviews,
  type SegmentationPreview,
} from "../predictions/segmentationPreviews";
import {
  persistedSegmentationCategory,
  segmentationConditionLabels,
} from "../predictions/segmentationCondition";
import { renalPredictionsToReportStones } from "../stones/predictionReportAdapter";
import {
  resolveSegmentationSidebarMode,
  shouldShowSegmentationStudyPredictions,
  type SegmentationAnnotationMode,
} from "../predictions/segmentationSidebarMode";
import { usePredictionMaskOverlay } from "../hooks/usePredictionMaskOverlay";
import { logger } from "../utils/logger";
import { maskSliceVolumeIndices } from "../utils/predictionMaskOverlay";
import { reportPrioritySeriesId } from "../utils/reportPrioritySeries";
import { candidateVerdictKey, useCandidateVerdicts } from "../hooks/useCandidateVerdicts";
import { useSourceVolumeLoaded } from "../hooks/useSourceVolumeLoaded";
import {
  VerdictNotSentError,
  describeVerdictFailure,
  verdictFailureToast,
} from "../predictions/verdictError";
import {
  READER_CONFIDENCE_KEY,
  acceptedPredictionAttributes,
  parseReaderConfidence,
  requireAcceptReaderConfidence,
} from "../utils/readerConfidence";

/** A window (level and width) to show a pane at. */
export interface WindowLevel {
  center: number;
  width: number;
}

export interface CaseViewerProps {
  studyData: {
    studyId: string;
    /** Optional worklist hint used while choosing the initial series. */
    modelSelectedSeriesId?: string;
    /** `+`-joined analysed-series ids used to resolve prediction geometry. */
    analyzedSeriesId?: string;
    /** Worklist model identity available before prediction data loads. */
    device_id?: string;
    /** Worklist flag: released triage device row (renders the notification panel). */
    triageDevice?: boolean;
  };
  enableSegmentation?: boolean;
  /** Labeling project that owns the embedded segmentation annotation panel. */
  projectId?: string;
  // Short review cohort id (e.g. `rdn60`), threaded from the review worklist
  // via `?cohort=` so the reviewer panel can read/write dispositions. Distinct
  // from the reporter cohort parsed out of the jobId.
  reviewCohortId?: string;
  /** Reader comparison cohort; distinct from the image inference job's cohort. */
  readerCohortId?: string;
  /** Optional worklist-seeded prediction used by cohort-backed study arms. */
  modelPredictionOverride?: ModelPredictionResponse | null;
  /** Site triage worklist routing for this study. A study that was not routed has no
   * device output: no prediction is fetched and only the routing reason is shown. */
  triageRouting?: { device: string; routed: boolean; reason: string; ageRecorded: boolean };
  /** Cohort-backed annotation studies have no reporter row/report endpoint. */
  disableReportQueries?: boolean;
  /** Reporter-style shell shown around a segmentation-study preview rail. */
  segmentationStudyContext?: {
    templateMode: "ai" | "model" | "blank";
    annotationConditions: string[];
    caseLabel: string;
    currentIndex: number;
    totalCases: number;
    hasPrevious: boolean;
    hasNext: boolean;
    isComplete: boolean;
    isMarkingComplete: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onNextUnlabeled?: () => void;
    hasNextUnlabeled?: boolean;
    onWorklist: () => void;
    onMarkComplete: () => void;
    /** Cards are locked while set: the case is complete or flagged unassessable by this reader. */
    lockReason?: CaseLockReason | null;
  };
  /** Factory cohort annotation shell: same segmentation workflow, without study timing. */
  factorySegmentationContext?: {
    caseId: string;
    annotationConditions: string[];
    caseLabel: string;
    currentIndex: number;
    totalCases: number;
    hasPrevious: boolean;
    hasNext: boolean;
    isComplete: boolean;
    isMarkingComplete: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onNextUnlabeled?: () => void;
    hasNextUnlabeled?: boolean;
    onWorklist: () => void;
    onMarkComplete: () => void;
  };
  /**
   * Viewer surfaces of the hosting page. A labeling host passes its own so the
   * segmentation surface (and its no-snap camera gate) survives this mount;
   * defaults to dictation.
   */
  baseSurfaces?: readonly Surface[];
  /** Host-owned right rail, checked before every built-in panel. The host carries
   * the device output, so the viewer fetches no prediction of its own. It gets a
   * jump to the slice holding a SOP Instance UID (a device's key slice), shown at
   * the window the rail names for the finding. */
  rightRail?: (
    jumpToSlice: (sopInstanceUid: string, window: WindowLevel) => void,
  ) => React.ReactNode;
  /** SOP Instance UIDs a host rail flags; each is ticked on the slice bar. */
  keySlices?: readonly string[];
  /** The host's own images route (a device panel's or a joint review's): the
   * images are read through it (`studyId` is then the study UID), never through
   * a device's job. */
  imagesUrl?: string;
  /** The segmentation study's grid (axial / coronal / sagittal, bottom-right
   * empty, always 2×2) for a host with no annotation context, such as a joint
   * review. Opens no drawing tools. */
  segmentationLayout?: boolean;
  /** The host's series for the axial pane; it wins over every other priority. */
  prioritySeriesId?: string | null;
}

// Series list comes from backend metadata (useDicomSeriesLoader.studyMetadata.series)
const CaseViewer: React.FC<CaseViewerProps> = ({
  studyData,
  enableSegmentation = false,
  projectId = "viewer",
  reviewCohortId,
  readerCohortId,
  modelPredictionOverride,
  disableReportQueries = false,
  segmentationStudyContext,
  factorySegmentationContext,
  triageRouting,
  baseSurfaces = DEFAULT_CASE_VIEWER_SURFACES,
  rightRail,
  keySlices,
  imagesUrl,
  segmentationLayout = false,
  prioritySeriesId,
}) => {
  const { userId, token, userType } = useAuth();
  // Global show/hide for the AI prediction mask overlay, so a reader can
  // inspect the raw pixels underneath without rejecting the finding (viewing
  // must never be conflated with a verdict). Per-study state: always reset to
  // visible on study change so no case silently opens with masks hidden.
  const [predictionMasksVisible, setPredictionMasksVisible] = useState(true);
  const togglePredictionMasks = useCallback(() => {
    setPredictionMasksVisible((visible) => !visible);
  }, []);
  useEffect(() => {
    setPredictionMasksVisible(true);
  }, [studyData.studyId]);
  // Which right-sidebar panel a reviewer sees. Reviewers normally get the
  // disposition panel, but customer-pilot companies (e.g. HT Médica) consume
  // findings as a report — they stay user_type=reviewer for cohort-scoped data
  // access but the case-viewer renders PrototypeReport instead. Gated on the
  // JWT `company_id` claim; see reporterViewGate.
  const showReviewerPanel = useMemo(
    () => shouldShowReviewerPanel({ userType, companyId: companyIdFromToken(token) }),
    [userType, token],
  );
  const { terminateAllWorkers } = useWorker();
  const setHoveredSurface = useSetAtom(hoveredSurfaceAtom);
  useEffect(
    () => () => setHoveredSurface((prev) => (prev === "viewer" ? null : prev)),
    [setHoveredSurface],
  );
  // Model prediction: immutable inference data (cached indefinitely). Used
  // as the `preferredSeriesId` hint into the series loader; consumed
  // directly so we don't have to mirror it back into local state via a
  // sync useEffect.
  const {
    data: fetchedModelPrediction,
    isLoading: predictionLoading,
    isError: predictionError,
  } = useModelPrediction(token, userId, studyData.studyId, {
    // A host rail leaves the viewer with no prediction and no device id, so
    // modelIdentityMissing keeps every viewer-side AI surface off.
    enabled: triageRouting?.routed !== false && !rightRail,
  });
  const triageNotRouted = triageRouting?.routed === false;
  const modelPrediction = modelPredictionOverride ?? fetchedModelPrediction;
  const isNeuroResult =
    isNeuroModel(studyData.device_id) || isNeuroModel(modelPrediction?.device_id);
  // Released triage device: the cleared output is a notification, not image
  // markings, so every research prediction surface (masks, marks, report, bbox)
  // stays off and the sidebar shows only the device's flags.
  const isTriageResult =
    triageNotRouted || !!studyData.triageDevice || !!modelPrediction?.triage_review;
  const segmentationAnnotationContext = segmentationStudyContext ?? factorySegmentationContext;
  // ENG-188: the reader study (both arms) must not reach nnInteractive
  // (non-commercial model licence). Region grow falls back to the threshold.
  const isSegmentationStudy = !!segmentationStudyContext;
  // ENG-202: a closed study case (complete / own unassessable flag) is read-only.
  const caseLocked = !!segmentationStudyContext?.lockReason;
  const setNnInteractiveBlocked = useSetAtom(nnInteractiveBlockedAtom);
  // ENG-280: the study also saves one record per stone, with no companion masks.
  const setCompanionMasksBlocked = useSetAtom(companionMasksBlockedAtom);
  useEffect(() => {
    if (!isSegmentationStudy) return;
    setNnInteractiveBlocked(true);
    setCompanionMasksBlocked(true);
    return () => {
      setNnInteractiveBlocked(false);
      setCompanionMasksBlocked(false);
    };
  }, [isSegmentationStudy, setNnInteractiveBlocked, setCompanionMasksBlocked]);
  const conditionLabels = useMemo(
    () => segmentationConditionLabels(segmentationAnnotationContext?.annotationConditions ?? []),
    [segmentationAnnotationContext?.annotationConditions],
  );
  const annotationCategories = useMemo(() => Object.keys(conditionLabels), [conditionLabels]);
  // Cold reader links initially have neither a cohort row nor a prediction.
  // Keep DICOM loading independent, but do not infer a report workflow from missing identity.
  const modelIdentityMissing =
    !triageRouting &&
    !segmentationAnnotationContext &&
    !studyData.device_id &&
    !modelPrediction?.device_id;
  const showSegmentationStudyPredictions = shouldShowSegmentationStudyPredictions(
    segmentationStudyContext?.templateMode,
  );

  // useDicomSeriesLoader owns its loading state via selectionAtoms — caller
  // only passes per-case identity + auth. Atom-backed series state means
  // there's one writer per field; pre-atomize this hook took `setStudy`
  // and both the hook and the caller could write the same field.
  const loadedSeries = useAtomValue(loadedSeriesAtom);
  const { selectedSeriesId, studyMetadata, studyNotCanonicalized, imagesError, loadingStage } =
    useDicomSeriesLoader({
      studyId: studyData.studyId,
      modelSelectedSeriesId: studyData.modelSelectedSeriesId,
      userId: userId || "",
      token: token || null,
      preferredSeriesId: modelPrediction?.selectedSeriesId ?? null,
      imagesUrl,
    });

  // Slice navigation state shared via Jotai atoms (used by VolumeViewport)
  const [currentSlice, setCurrentSlice] = useAtom(currentSliceAtom);
  const totalSlices = useAtomValue(totalSlicesAtom);
  const instanceSliceInfoMap = useAtomValue(instanceSliceInfoMapAtom);
  const neuroTargets = useMemo(
    () => neuroFindingTargets(modelPrediction?.neuro_review, studyMetadata),
    [modelPrediction?.neuro_review, studyMetadata],
  );

  // Slice-bar marks — resolved once per study from the base model's predictions,
  // or from a host rail's key slices.
  // CaseViewer is the only place holding both the predictions and the
  // UID→(series, slice) map, so it owns the resolve; the viewports only read.
  const setSegmentationMarks = useSetAtom(segmentationMarksAtom);
  // The blank study arm is intentionally prediction-blind. Gate the shared
  // source payload here so cards, locked mask contours, and finder marks all
  // disappear together instead of relying on three independent UI checks.
  const basePredictions =
    showSegmentationStudyPredictions && !isTriageResult
      ? modelPrediction?.base_model_prediction
      : undefined;
  const [localStoneVerdicts, setStoneVerdicts] = useAtom(stoneVerdictsAtom);
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const factoryAnnotationsQuery = useAnnotationsQuery(
    studyData.studyId,
    segmentationAnnotationContext ? projectId : "",
  );
  const candidateVerdicts = useCandidateVerdicts(
    projectId,
    studyData.studyId,
    !!segmentationAnnotationContext,
  );
  // Shared by the Edit button and its click (ENG-187). Our own writes already
  // hold decisions not-ready until both reloads finish.
  const editBlockedBySavedAnnotations =
    !!segmentationAnnotationContext && savedAnnotationsBlocked(factoryAnnotationsQuery);
  // Shared by every card action and its click (ENG-186 step 2): the labelmap
  // exists before the CT pixels stream in, so actions wait for the full load.
  const sourceVolumeState = useSourceVolumeLoaded(segmentationVolumeId);
  const imagesLoading = !!segmentationAnnotationContext && sourceVolumeState === "loading";
  const imagesLoadUnknown = !!segmentationAnnotationContext && sourceVolumeState === "unknown";
  const persistCandidateVerdict = candidateVerdicts.setVerdict;
  const candidateEditHead = useRef<{ scope: string; key: string; eventId: string | null } | null>(
    null,
  );
  const candidateScope = JSON.stringify([projectId, studyData.studyId.split("::")[0]]);
  const startCandidateEdit = useCallback(
    (condition: string, source: number) => {
      if (candidateVerdicts.isCandidateLocked(condition, source)) return false;
      const event = candidateVerdicts.verdicts.find(
        (item) => item.condition === condition && item.source_segment_index === source,
      );
      candidateEditHead.current = {
        scope: candidateScope,
        key: candidateVerdictKey(condition, source),
        eventId: event?.event_id ?? null,
      };
      return true;
    },
    [candidateVerdicts.isCandidateLocked, candidateVerdicts.verdicts, candidateScope],
  );
  const saveCandidateAnnotation = useCallback(
    (annotation: AnnotationInput) => {
      const observed = candidateEditHead.current;
      const key = candidateVerdictKey(
        String(annotation.attributes.ai_prediction_condition),
        Number(annotation.attributes.ai_prediction_source_segment_index),
      );
      if (!observed || observed.scope !== candidateScope || observed.key !== key) {
        throw new Error("Reopen the candidate editor before saving");
      }
      return candidateVerdicts.saveAnnotation(annotation, observed.eventId);
    },
    [candidateVerdicts.saveAnnotation, candidateScope],
  );
  useEffect(() => {
    const warn = (message: string) => logger.warn(message);
    setSegmentationMarks(
      keySlices
        ? resolveKeySliceMarks(keySlices, instanceSliceInfoMap, warn)
        : resolveSegmentationMarks(
            basePredictions ?? {},
            instanceSliceInfoMap,
            studyData.analyzedSeriesId,
            warn,
          ),
    );
  }, [
    keySlices,
    basePredictions,
    instanceSliceInfoMap,
    studyData.analyzedSeriesId,
    setSegmentationMarks,
  ]);

  // Pixel-accurate model mask. The engine keys each RLE plane by SOP UID;
  // resolve it to a concrete series/index here, then the headless renderer
  // registers locked Cornerstone contours as viewports become ready.
  const predictionMask = useMemo(
    () =>
      resolvePredictionMask(
        basePredictions ?? {},
        instanceSliceInfoMap,
        studyData.analyzedSeriesId,
        (message) => logger.warn(message),
      ),
    [basePredictions, instanceSliceInfoMap, studyData.analyzedSeriesId],
  );

  const gpuVerdict = useAtomValue(gpuVerdictAtom);
  const predictionSegmentIndices = useMemo(() => {
    if (showSegmentationStudyPredictions && !modelPrediction) return undefined;
    // Reserve the full potential range, including predictions whose DICOM slices
    // have not resolved yet. resolvePredictionMask assigns consecutive indices.
    const count = Object.values(basePredictions ?? {}).reduce(
      (total, prediction) => total + (prediction.segmentation?.segments.length ?? 0),
      0,
    );
    return Array.from({ length: count }, (_, index) => index + 1);
  }, [basePredictions, modelPrediction, showSegmentationStudyPredictions]);
  // Selection state shared via Jotai atom (used by PrototypeReport for slice navigation)

  const [studyUid, jobId = ""] = studyData.studyId.split("::");
  const isDrafterResult = jobId.toLowerCase().includes("drafter");

  // Surfacer mode: per-finding verifier surface in the right sidebar.
  // Auto-enabled when the reader-ingest Lambda has written a `comparison` SK
  // row for this study — that's the DDB-backed flag for "reporter cohort".
  // The /surfacer endpoint 404s when the row is missing (cortex/drafter), so
  // `surfacerData` ends up null and the default panels render instead.
  const { data: surfacerData, isLoading: surfacerLoading } = useSurfacerCandidates(
    isDrafterResult || isNeuroResult || isTriageResult || modelIdentityMissing ? null : studyUid,
    isDrafterResult || isNeuroResult || isTriageResult || modelIdentityMissing ? null : jobId,
    readerCohortId,
  );
  const surfacerMode = !!surfacerData;
  // Gate for downstream hooks whose panels are unreachable in surfacer mode.
  // Holds true on first render so we don't kick off an orphan fetch that
  // surfacerData will then invalidate ~50ms later.
  const skipNonSurfacerHooks =
    surfacerMode || surfacerLoading || isNeuroResult || isTriageResult || modelIdentityMissing;

  // Reporter comparison data — overlay behavior (shows ComparisonPanel if
  // data exists). Skipped in surfacer mode because the surfacer endpoint is
  // a server-side filtered view of the same `comparison` SK row.
  const { data: reporterComparison } = useReporterComparison(
    isDrafterResult || skipNonSurfacerHooks ? null : studyUid,
    isDrafterResult || skipNonSurfacerHooks ? null : jobId,
    readerCohortId,
  );

  // Reset all per-case atoms on mount in lockstep so prior-case selection +
  // segmentation state doesn't bleed across study switches. useLayoutEffect
  // ensures this runs synchronously before first paint.
  const resetCaseAtoms = useSetAtom(resetCaseAtomsAtom);
  useLayoutEffect(() => {
    resetCaseAtoms();
  }, [resetCaseAtoms]);
  useAnnotationSelectionMirror();

  // Findings list mirror — populated from useReport so Tab cycling can iterate
  // findings via advanceFocusedFinding without re-walking the section tree.
  // Skipped in surfacer mode: PrototypeReport is replaced by
  // OriginalReportPanel and the verifier navigates via the SurfacerPanel
  // queue, not Tab cycling.
  const { data: report, isLoading: reportIsLoading } = useReport(
    skipNonSurfacerHooks || disableReportQueries ? null : studyData.studyId,
  );
  const setOrderedFindings = useSetAtom(orderedFindingsAtom);
  const orderedFindings = useAtomValue(orderedFindingsAtom);
  const setFocusedFindingIndex = useSetAtom(focusedFindingIndexAtom);
  useEffect(() => {
    if (!report) return;
    setOrderedFindings(Object.values(report.sections).flatMap((section) => section.findings));
  }, [report, setOrderedFindings]);

  // Fix C: once findings are known, warm the cornerstone cache for finding-series
  // in the background (finding slice first) so clicking a finding lands instantly
  // instead of cold-loading the whole series. No-op until findings + metadata
  // resolve; conservative so it never starves the active panes.
  useFindingSeriesPreload(studyData.studyId);

  // User-selected series from SeriesPicker (when no model-selected series exists)
  const [userSelectedSeriesId, setUserSelectedSeriesId] = useState<string | null>(null);

  // Excerpt to highlight + scroll-into-view in the surfacer-mode Original Report
  // panel. Set when a SurfacerPanel card is clicked.
  const [highlightExcerpt, setHighlightExcerpt] = useState<string | null>(null);

  // rdn60 stone-overlay demo: for allowlisted studies, force the series the
  // stone was segmented on into the priority pane, so the saved mask (defined
  // in that series' voxel grid) renders correctly. Without this the reporter
  // shows a different series and the contour can't align. React-query dedupes
  // this with StoneOverlayLoader's identical fetch.
  const stoneProjectId = getStoneOverlayProjectId(studyData.studyId);
  const stoneBareUid = stoneProjectId ? studyData.studyId.split("::")[0] : "";
  const stoneAnnotationsQuery = useAnnotationsQuery(stoneBareUid, stoneProjectId ?? "");
  // The "longest dimension" stone (largest convex-hull axis) — the annotation
  // the measurement, force-show and the jump all target. Single-stone cases
  // have one; bilateral picks the larger stone.
  const stonePrimaryAnnotation = useMemo(() => {
    const anns = stoneAnnotationsQuery.data?.annotations;
    if (!anns?.length) return null;
    let best = anns[0];
    for (const a of anns) {
      if ((a.hull_stats?.axis_1_mm ?? 0) > (best.hull_stats?.axis_1_mm ?? 0)) best = a;
    }
    return best;
  }, [stoneAnnotationsQuery.data]);
  const stoneAnnotationSeries = stonePrimaryAnnotation?.series_instance_uid?.split("::")[0] ?? null;

  const reportPrioritySeries = useMemo(() => reportPrioritySeriesId(report), [report]);

  // Resolved priority: the host's series > forced stone series > explicit model
  // selection > the series carrying the most report evidence > user pick.
  const resolvedPrioritySeriesId =
    prioritySeriesId ??
    stoneAnnotationSeries ??
    modelPrediction?.selectedSeriesId ??
    reportPrioritySeries ??
    userSelectedSeriesId;

  // UI layout states
  // Windowing state — preset + center/width travel together; bundled so
  // we set them in one shot from handleWindowPreset and onWindowChange.
  const [windowing, setWindowing] = useState<{
    preset: WindowPreset | null;
    center: number;
    width: number;
  }>({ preset: "soft_tissue", ...DEFAULT_WINDOWING });

  // Sidebar collapsed states (from Jotai atoms - persisted to localStorage)
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useAtom(leftSidebarCollapsedAtom);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useAtom(rightSidebarCollapsedAtom);
  const [reportSidebarWidth, setReportSidebarWidth] = useAtom(rightSidebarWidthAtom);
  const [triagePanelWidth, setTriagePanelWidth] = useAtom(triagePanelWidthAtom);
  // The triage notification panel keeps its own (narrower) width; every other
  // right-sidebar panel shares the report sidebar width as before.
  const rightSidebarWidth = isTriageResult ? triagePanelWidth : reportSidebarWidth;
  const setRightSidebarWidth = isTriageResult ? setTriagePanelWidth : setReportSidebarWidth;

  // Click a series thumbnail → load it into the active hanging-protocol pane.
  // Same wiring the labeling viewer uses (LabelingPage.handleSeriesSelect); it
  // was never connected on this dictation surface, so clicking a thumbnail did
  // nothing. Works in the 2x2 HangingProtocolGrid path (the sidebar's advertised
  // "Drag to viewport to assign" also works there).
  const dispatchHangingProtocol = useSetAtom(hangingProtocolActionsAtom);
  const activeSlot = useAtomValue(activeSlotAtom);
  const handleSeriesSelect = useCallback(
    (seriesId: string) => {
      if (!studyMetadata?.series_metadata) return;
      const seriesMeta = studyMetadata.series_metadata[seriesId];
      const seriesWindowing = seriesMeta ? getSeriesWindowing(seriesMeta) : undefined;
      const seriesInstances = studyMetadata.series?.[seriesId];
      warnOnMixedSignedTransferSyntax(seriesId, seriesInstances);
      dispatchHangingProtocol({
        type: "SET_SERIES",
        slotId: activeSlot,
        seriesId,
        windowing: seriesWindowing,
        isInverted: shouldAutoInvert(seriesInstances),
      });
    },
    [activeSlot, studyMetadata?.series_metadata, studyMetadata?.series, dispatchHangingProtocol],
  );

  // Drag-to-resize the report pane. Pointer capture lets us track movement
  // even when the cursor leaves the handle (Cornerstone canvases steal events).
  const handleRightSidebarResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = rightSidebarWidth;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        // Pane is on the right; dragging left grows it.
        const next = startWidth + (startX - moveEvent.clientX);
        const clamped = Math.max(RIGHT_SIDEBAR_MIN_WIDTH, Math.min(RIGHT_SIDEBAR_MAX_WIDTH, next));
        setRightSidebarWidth(clamped);
      };
      const onUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
      };
      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
    },
    [rightSidebarWidth, setRightSidebarWidth],
  );

  // Surfacer-mode vertical split between SurfacerPanel (top) and
  // OriginalReportPanel (bottom). Same pointer-capture pattern as the
  // sidebar resize handle above.
  const [surfacerSplit, setSurfacerSplit] = useAtom(surfacerSplitAtom);
  const handleSurfacerSplitResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      const container = target.parentElement;
      if (!container) return;
      target.setPointerCapture(e.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        const rect = container.getBoundingClientRect();
        const fraction = (moveEvent.clientY - rect.top) / rect.height;
        setSurfacerSplit(Math.max(SURFACER_SPLIT_MIN, Math.min(SURFACER_SPLIT_MAX, fraction)));
      };
      const onUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
      };
      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
    },
    [setSurfacerSplit],
  );

  // reporter-jobs reskin: the right sidebar is the Report and only the
  // Report. Comparison is folded into the report via underlined AI phrases;
  // segmentation/annotate is no longer surfaced here. The dead tab switcher
  // and override state were removed.
  // The tab is stateful so either the legacy finding rail or generic model
  // segmentations can coexist with the report.
  const [rightSidebarTab, setRightSidebarTab] = useState<
    "report" | "annotate" | "comparison" | "findings"
  >(enableSegmentation ? "findings" : "report");
  const [showAnnotationEditor, setShowAnnotationEditor] = useState(false);
  const [savedEditRequest, setSavedEditRequest] = useState<{
    annotationId: string;
    requestId: number;
  } | null>(null);
  const updateSavedAnnotation = useUpdateAnnotationMutation(projectId, studyData.studyId);
  useEffect(() => {
    setSavedEditRequest(null);
  }, [projectId, studyData.studyId]);
  const [segmentationAnnotationMode, setSegmentationAnnotationMode] =
    useState<SegmentationAnnotationMode>("idle");
  const [editingSegmentationPreviewId, setEditingSegmentationPreviewId] = useState<string | null>(
    null,
  );
  const predictionEditActions = useRef<PredictionEditActions | null>(null);
  const { stones, modelVersion: stoneModelVersion } = useStoneFindingsQuery(studyData.studyId);
  const predictionProbabilities = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(basePredictions ?? {}).map(([condition, prediction]) => [
          condition,
          prediction.probability ?? 0,
        ]),
      ),
    [basePredictions],
  );
  const segmentationPreviews = useMemo(
    () =>
      buildSegmentationPreviews(
        predictionMask,
        studyMetadata,
        predictionProbabilities,
        studyData.studyId,
      ),
    [predictionMask, studyMetadata, predictionProbabilities, studyData.studyId],
  );
  const acceptedPredictionAnnotationIds = useMemo(
    () =>
      Object.fromEntries(
        candidateVerdicts.verdicts
          .filter((event) => event.verdict === "accepted")
          .map((event) => [
            candidateVerdictKey(event.condition, event.source_segment_index),
            event.annotation_id,
          ]),
      ),
    [candidateVerdicts.verdicts],
  );
  const savedCardGeometry = useSavedCardGeometry(
    factoryAnnotationsQuery.data?.mask_url,
    factoryAnnotationsQuery.dataUpdatedAt,
  );
  const { additional: additionalSavedAnnotations, byPreviewId: predictionCardAnnotations } =
    useMemo(
      () =>
        getSegmentationCardAnnotations({
          annotations: (factoryAnnotationsQuery.data?.annotations ?? []).map((annotation) => ({
            ...annotation,
            card_geometry:
              annotation.card_geometry ?? savedCardGeometry.data?.[annotation.segment_index],
          })),
          previews: segmentationPreviews,
          linkedAnnotationIds: acceptedPredictionAnnotationIds,
          projectId,
          studyUid: studyData.studyId,
        }),
      [
        factoryAnnotationsQuery.data?.annotations,
        savedCardGeometry.data,
        segmentationPreviews,
        acceptedPredictionAnnotationIds,
        projectId,
        studyData.studyId,
      ],
    );
  const segmentationCardCount = segmentationPreviews.length + additionalSavedAnnotations.length;
  const savedPredictionAnnotations = useMemo(() => {
    const annotationsById = new Map(
      (factoryAnnotationsQuery.data?.annotations ?? []).map((annotation) => [
        annotation.annotation_id,
        annotation,
      ]),
    );
    return new Map(
      Object.entries(acceptedPredictionAnnotationIds).map(([candidateKey, annotationId]) => [
        candidateKey,
        annotationId ? annotationsById.get(annotationId) : undefined,
      ]),
    );
  }, [acceptedPredictionAnnotationIds, factoryAnnotationsQuery.data?.annotations]);
  const hasUnlinkedAiAnnotations = factoryAnnotationsQuery.data?.annotations.some(
    (annotation) =>
      annotation.attributes?.ai_prediction_review &&
      !Number.isSafeInteger(annotation.attributes?.ai_prediction_source_segment_index),
  );
  const persistedCandidateVerdicts = useMemo(
    () =>
      Object.fromEntries(
        candidateVerdicts.verdicts.map((event) => [
          candidateVerdictKey(event.condition, event.source_segment_index),
          event.verdict,
        ]),
      ),
    [candidateVerdicts.verdicts],
  );
  const stoneVerdicts = useMemo(() => {
    // Reader studies and factory annotation share persisted candidate decisions.
    if (!segmentationAnnotationContext) return localStoneVerdicts;
    return Object.fromEntries(
      segmentationPreviews.map((preview) => {
        const saved =
          persistedCandidateVerdicts[
            candidateVerdictKey(preview.condition, preview.sourceSegmentIndex)
          ];
        return [
          preview.id,
          saved === "accepted"
            ? ("approved" as const)
            : saved === "rejected"
              ? ("denied" as const)
              : ("pending" as const),
        ];
      }),
    );
  }, [
    segmentationAnnotationContext,
    localStoneVerdicts,
    segmentationPreviews,
    persistedCandidateVerdicts,
  ]);

  // The AI card whose decision is unconfirmed; the rail dims every other card (ENG-186).
  const unconfirmed = candidateVerdicts.unconfirmedCandidate;
  const unconfirmedPreviewId = unconfirmed
    ? (segmentationPreviews.find(
        (preview) =>
          candidateVerdictKey(preview.condition, preview.sourceSegmentIndex) ===
          candidateVerdictKey(unconfirmed.condition, unconfirmed.source_segment_index),
      )?.id ?? null)
    : null;

  // Cards whose confirmed save is still reloading; the rest of the rail stays usable (ENG-229).
  const lockedPreviewIds = useMemo(
    () =>
      new Set(
        segmentationPreviews
          .filter((preview) =>
            candidateVerdicts.isCandidateLocked(preview.condition, preview.sourceSegmentIndex),
          )
          .map((preview) => preview.id),
      ),
    [segmentationPreviews, candidateVerdicts.isCandidateLocked],
  );

  const acceptPrediction = useCallback(
    async (preview: SegmentationPreview, cardReaderConfidence?: number) => {
      if (!segmentationAnnotationContext) return;
      if (candidateVerdicts.isCandidateLocked(preview.condition, preview.sourceSegmentIndex))
        throw new VerdictNotSentError("decisions-not-ready", "Saved decisions are not ready");
      // The rail hides Accept for these, so reaching here means an affordance
      // regression; refuse rather than send a category the backend's
      // annotation validator would 422.
      const persistedCategory = persistedSegmentationCategory(preview.condition);
      if (persistedCategory == null) {
        throw new VerdictNotSentError(
          "no-schema",
          `Condition ${preview.condition} has no persisted annotation schema; cannot accept`,
        );
      }
      if (!predictionMask || !segmentationVolumeId) {
        throw new VerdictNotSentError("volume-not-ready", "Prediction mask volume is not ready");
      }
      const segment = predictionMask.segments.find(
        (candidate) => candidate.segmentIndex === preview.segmentIndex,
      );
      if (!segment)
        throw new VerdictNotSentError(
          "segment-missing",
          `Prediction segment ${preview.segmentIndex} is missing`,
        );

      // Accept on the card being edited runs Save first (ENG-185): changed geometry
      // is saved as "modified"; an unchanged edit closes and accepts the prediction.
      // Study arm (ENG-262): the rating comes from the card, or from the open
      // editor when Accept runs on the card being edited.
      let readerConfidence: number | null = null;
      if (editingSegmentationPreviewId === preview.id) {
        const actions = predictionEditActions.current;
        if (!actions)
          throw new VerdictNotSentError("editor-not-ready", "The candidate editor is not ready");
        if (isSegmentationStudy)
          readerConfidence = requireAcceptReaderConfidence(actions.readerConfidence);
        if ((await actions.accept()) !== "unchanged") return;
      } else if (isSegmentationStudy) {
        readerConfidence = requireAcceptReaderConfidence(cardReaderConfidence);
      }

      const cornerstone = getCornerstone();
      const volume = cornerstone.cache.getVolume(segmentationVolumeId) as
        | {
            dimensions?: [number, number, number];
            spacing?: [number, number, number];
            origin?: ArrayLike<number>;
            direction?: ArrayLike<number>;
            referencedVolumeId?: string;
          }
        | undefined;
      const dimensions = volume?.dimensions;
      const voxelSpacing = volume?.spacing;
      if (!dimensions || !voxelSpacing)
        throw new VerdictNotSentError("volume-missing", "Segmentation volume geometry is missing");
      // The mask grid may differ from the series' (model fixed at 512²); the
      // labelmap is scaled below. What must match is the series (ENG-278).
      const sizes =
        `prediction mask ${predictionMask.columns}×${predictionMask.rows}, ` +
        `annotation volume ${dimensions[0]}×${dimensions[1]}, series ${preview.seriesInstanceUid}`;
      const sourceImageIds = volume.referencedVolumeId
        ? (cornerstone.cache.getVolume(volume.referencedVolumeId) as { imageIds?: string[] })
            ?.imageIds
        : undefined;
      // Slices are written in the volume's own order, like Save (ENG-291).
      const slicesInVolume = maskSliceVolumeIndices(segment, sourceImageIds);
      if (slicesInVolume === "unknown") {
        logger.warn(`[segmentation-preview] accept: cannot check the mask's series (${sizes})`);
      } else if (slicesInVolume.missing.length > 0) {
        const { missing } = slicesInVolume;
        throw new VerdictNotSentError(
          "series-mismatch",
          `${missing.length} of ${segment.slices.length} mask slices are not in the annotation ` +
            `volume (${sizes}; first missing ${missing[0].sliceId})`,
        );
      }

      const labelmap = predictionMaskSegmentToLabelmap(
        segment,
        predictionMask.rows,
        predictionMask.columns,
        dimensions[1],
        dimensions[0],
        slicesInVolume === "unknown"
          ? undefined
          : (slice) => slicesInVolume.volumeIndex.get(slice) ?? slice.sliceIndex,
      );
      // A native grid coarser than the mask can sample past a thin stone.
      if (Object.keys(labelmap).length === 0)
        throw new VerdictNotSentError(
          "segment-missing",
          `Prediction segment ${preview.segmentIndex} has no voxels on the native grid (${sizes})`,
        );

      const now = new Date().toISOString();
      const payload: AnnotationInput = {
        project_id: projectId,
        study_instance_uid: studyData.studyId.split("::")[0],
        series_instance_uid: preview.seriesInstanceUid.split("::")[0],
        category: persistedCategory,
        labelmap,
        measurements: {
          long_axis_mm: preview.measurements.longAxisMm ?? 0,
          short_axis_mm: preview.measurements.shortAxisMm ?? 0,
        },
        hu_stats: { hu_min: 0, hu_max: 0, hu_avg: 0 },
        attributes: acceptedPredictionAttributes(preview, readerConfidence),
        timing: { start_ts: now, end_ts: now, active_seconds: 0 },
        contour_stats: {
          total_slices: segment.slices.length,
          manual_slices: 0,
          interpolated_slices: 0,
          bumped_slices: 0,
          region_grow_slices: 0,
          source_series_uid: preview.seriesInstanceUid.split("::")[0],
        },
        volume_shape: [dimensions[1], dimensions[0], dimensions[2]],
        spacing: [voxelSpacing[1], voxelSpacing[0], voxelSpacing[2]],
        volume_origin: volume.origin
          ? [volume.origin[0], volume.origin[1], volume.origin[2]]
          : undefined,
        volume_direction: volume.direction ? Array.from(volume.direction, Number) : undefined,
      };
      await persistCandidateVerdict({
        condition: preview.condition,
        source_segment_index: preview.sourceSegmentIndex,
        verdict: "accepted",
        annotation: payload,
      });
    },
    [
      segmentationAnnotationContext,
      candidateVerdicts.isCandidateLocked,
      predictionMask,
      segmentationVolumeId,
      projectId,
      studyData.studyId,
      persistCandidateVerdict,
      editingSegmentationPreviewId,
      isSegmentationStudy,
    ],
  );

  // Attributes-only update (ENG-277): keeps the mask and ai_prediction_review, and
  // sends no time event. The PUT replaces attributes wholesale, so send them all.
  const saveReaderConfidence = useCallback(
    async (annotation: AnnotationRecordResponse, readerConfidence: number) => {
      if (caseLocked) throw new Error("The case is locked");
      await updateSavedAnnotation.mutateAsync({
        annotationId: annotation.annotation_id,
        update: {
          attributes: { ...annotation.attributes, [READER_CONFIDENCE_KEY]: readerConfidence },
        },
      });
    },
    [caseLocked, updateSavedAnnotation.mutateAsync],
  );

  // Editor Save with unchanged geometry (ENG-277): keep the AI mask and its review label.
  const saveUnchangedPredictionEdit = useCallback(
    async (readerConfidence: number | null): Promise<"closed" | "failed"> => {
      const preview = segmentationPreviews.find(
        (candidate) => candidate.id === editingSegmentationPreviewId,
      );
      const actions = predictionEditActions.current;
      if (!preview || !actions) {
        emit("toast", { message: "The editor is not ready. Close it and try again." });
        return "failed";
      }
      const candidateKey = candidateVerdictKey(preview.condition, preview.sourceSegmentIndex);
      if (Object.hasOwn(acceptedPredictionAnnotationIds, candidateKey)) {
        // Already accepted: only the rating can change, through the attributes-only update.
        const saved = savedPredictionAnnotations.get(candidateKey);
        if (!saved) {
          emit("toast", {
            message: "The saved annotation could not be found. Please reload the case.",
          });
          return "failed";
        }
        const savedConfidence = parseReaderConfidence(saved.attributes?.[READER_CONFIDENCE_KEY]);
        if (readerConfidence != null && readerConfidence !== savedConfidence) {
          try {
            await saveReaderConfidence(saved, readerConfidence);
          } catch (error) {
            const failure = describeVerdictFailure(error);
            console.error("[segmentation-preview] confidence update failed", failure, error);
            emit("toast", {
              message: `Could not save the confidence. Your edit is still open; please try again. [${failure.code}]`,
            });
            return "failed";
          }
        }
        return actions.discard() ? "closed" : "failed";
      }

      // Not accepted yet: accept the AI mask as is, on any grid (ENG-278 scales it).
      try {
        await acceptPrediction(preview);
        return "closed";
      } catch (error) {
        const failure = describeVerdictFailure(error);
        console.error(
          "[segmentation-preview] accept failed",
          {
            case: failure.kind,
            code: failure.code,
            status: failure.status,
            detail: failure.detail,
            previewId: preview.id,
            condition: preview.condition,
            sourceSegmentIndex: preview.sourceSegmentIndex,
          },
          error,
        );
        emit("toast", { message: verdictFailureToast("accept", failure) });
        return "failed";
      }
    },
    [
      segmentationPreviews,
      editingSegmentationPreviewId,
      acceptedPredictionAnnotationIds,
      savedPredictionAnnotations,
      saveReaderConfidence,
      acceptPrediction,
    ],
  );
  const declinePrediction = useCallback(
    async (preview: SegmentationPreview) => {
      if (!segmentationAnnotationContext) return;
      // Declining the card being edited discards its unsaved edits first, so a
      // stale edit session cannot save after the verdict.
      if (editingSegmentationPreviewId === preview.id) {
        const actions = predictionEditActions.current;
        if (!actions)
          throw new VerdictNotSentError("editor-not-ready", "The candidate editor is not ready");
        if (!actions.discard()) return;
      }
      await persistCandidateVerdict({
        condition: preview.condition,
        source_segment_index: preview.sourceSegmentIndex,
        verdict: "rejected",
      });
    },
    [segmentationAnnotationContext, persistCandidateVerdict, editingSegmentationPreviewId],
  );
  const undoPredictionVerdict = useCallback(
    async (preview: SegmentationPreview) => {
      if (!segmentationAnnotationContext) return;
      await persistCandidateVerdict({
        condition: preview.condition,
        source_segment_index: preview.sourceSegmentIndex,
        verdict: "pending",
      });
    },
    [segmentationAnnotationContext, persistCandidateVerdict],
  );
  // Denied predictions and predictions replaced by saved masks drop out of the overlay; ids are
  // study-scoped so verdicts can't leak across studies. When the global
  // toggle hides masks, every segment goes through the same proven
  // hidden-segments reconciliation path the verdicts use.
  const hiddenPredictionSegmentIndices = useMemo(() => {
    if (!predictionMasksVisible) {
      return segmentationPreviews.map((preview) => preview.segmentIndex);
    }
    return segmentationPreviews
      .filter((preview) => {
        const savedAnnotation = savedPredictionAnnotations.get(
          candidateVerdictKey(preview.condition, preview.sourceSegmentIndex),
        );
        // Follows the loaded data, not the last request: the saved mask stays
        // drawn after a failed refetch, so its AI contour stays hidden too.
        const replacedBySavedMask =
          !!segmentationAnnotationContext &&
          !!factoryAnnotationsQuery.data?.mask_url &&
          !!savedAnnotation;
        return stoneVerdicts[preview.id] === "denied" || replacedBySavedMask;
      })
      .map((preview) => preview.segmentIndex);
  }, [
    segmentationPreviews,
    stoneVerdicts,
    predictionMasksVisible,
    savedPredictionAnnotations,
    segmentationAnnotationContext,
    factoryAnnotationsQuery.data?.mask_url,
  ]);
  // AI cards whose contour is hidden and that have no saved annotation to edit
  // instead: the editor's entry would wait forever (ENG-186 step 3). Saved
  // contours ignore "Hide masks", so linked cards stay editable. Shared by the
  // Edit button and its click.
  const editHiddenReasons = useMemo(() => {
    const reasons: Record<string, EditHiddenReason> = {};
    for (const preview of segmentationPreviews) {
      const key = candidateVerdictKey(preview.condition, preview.sourceSegmentIndex);
      if (acceptedPredictionAnnotationIds[key]) continue;
      if (stoneVerdicts[preview.id] === "denied") reasons[preview.id] = "declined";
      else if (!predictionMasksVisible) reasons[preview.id] = "masks-hidden";
    }
    return reasons;
  }, [
    segmentationPreviews,
    acceptedPredictionAnnotationIds,
    stoneVerdicts,
    predictionMasksVisible,
  ]);
  const predictionOverlay = usePredictionMaskOverlay(
    predictionMask,
    studyData.studyId,
    hiddenPredictionSegmentIndices,
    candidateVerdicts.ready &&
      factoryAnnotationsQuery.isSuccess &&
      !factoryAnnotationsQuery.isFetching &&
      !showAnnotationEditor
      ? segmentationPreviews
          .filter((preview) => {
            if (candidateVerdicts.isCandidateLocked(preview.condition, preview.sourceSegmentIndex))
              return false;
            const key = candidateVerdictKey(preview.condition, preview.sourceSegmentIndex);
            const event = candidateVerdicts.verdicts.find(
              (item) => candidateVerdictKey(item.condition, item.source_segment_index) === key,
            );
            return (
              !!event &&
              event.verdict !== "accepted" &&
              !event.annotation_id &&
              !savedPredictionAnnotations.get(key)
            );
          })
          .map((preview) => preview.segmentIndex)
      : [],
  );
  const { beginPredictionEdit, getPredictionEditCompletion, maskLoadStatus, maskLoadDeadlines } =
    predictionOverlay;
  // AI cards wait for their outline, so a click never lands before it (ENG-269).
  const [
    maskLoadingPreviewIds,
    maskFailedPreviewIds,
    maskUnavailablePreviewIds,
    maskLoadingDeadlines,
  ] = useMemo(() => {
    const byStatus = (status: string) =>
      new Set(
        segmentationPreviews
          .filter((preview) => maskLoadStatus[preview.segmentIndex] === status)
          .map((preview) => preview.id),
      );
    const deadlines: Record<string, number> = {};
    for (const preview of segmentationPreviews) {
      const deadline = maskLoadDeadlines[preview.segmentIndex];
      if (deadline != null) deadlines[preview.id] = deadline;
    }
    return [byStatus("loading"), byStatus("failed"), byStatus("unavailable"), deadlines];
  }, [segmentationPreviews, maskLoadStatus, maskLoadDeadlines]);
  const editingSegmentationPreview = useMemo(
    () =>
      editingSegmentationPreviewId
        ? (segmentationPreviews.find((preview) => preview.id === editingSegmentationPreviewId) ??
          null)
        : null,
    [editingSegmentationPreviewId, segmentationPreviews],
  );
  const reportStones = useMemo(
    () => (stones.length > 0 ? stones : renalPredictionsToReportStones(segmentationPreviews)),
    [stones, segmentationPreviews],
  );
  const sidebarFindingCount = stones.length || segmentationPreviews.length;
  const segmentationSidebarMode = resolveSegmentationSidebarMode({
    isSegmentationStudy: !!segmentationAnnotationContext,
    findingCount: stones.length,
    predictionCount: segmentationPreviews.length,
  });
  const predictionMaskControlAvailable =
    !modelIdentityMissing &&
    !isNeuroResult &&
    !isTriageResult &&
    !surfacerMode &&
    !showReviewerPanel &&
    !rightSidebarCollapsed &&
    rightSidebarTab === "findings" &&
    segmentationPreviews.length > 0 &&
    (segmentationSidebarMode === "prediction-readonly" ||
      segmentationSidebarMode === "segmentation-study");
  useCaseViewerSurfaces(baseSurfaces, predictionMaskControlAvailable);
  const sidebarFindingLabel =
    segmentationSidebarMode === "standard-findings" ? "Stones" : "Segmentations";
  // Auto-switch to findings once, from the untouched initial report. Stone
  // telemetry remains exclusive to the legacy renal-stone finding surface.
  const findingsDefaulted = useRef(false);
  useEffect(() => {
    if (!findingsDefaulted.current && sidebarFindingCount > 0) {
      findingsDefaulted.current = true;
      setRightSidebarTab("findings");
      if (stones.length > 0) {
        emitStoneEvent(
          buildStoneEvent("stone_surface_opened", {
            studyUid: studyData.studyId,
            detected: stones.length,
            modelVersion: stoneModelVersion ?? modelPrediction?.device_id ?? null,
          }),
        );
      }
    }
  }, [
    sidebarFindingCount,
    stones.length,
    studyData.studyId,
    stoneModelVersion,
    modelPrediction?.device_id,
  ]);

  // Report write-back for approved stones. The reporter editor is (un)mounted
  // with the Report tab, so we hold it in state + a memoized ref: the sync
  // re-runs when a fresh editor mounts (folding current verdicts into the
  // KIDNEYS/URETERS chip) and no-ops against a null ref while the Stones tab is
  // showing — never touching a destroyed editor.
  const [reportEditor, setReportEditor] = useState<Editor | null>(null);
  const reportEditorRef = useMemo<{ current: Editor | null }>(
    () => ({ current: reportEditor }),
    [reportEditor],
  );
  // Bumped when PrototypeReport (re)applies report content via setContent, which
  // wipes the injected stone chip; the bump re-runs the sync to re-inject it.
  const [reportDocVersion, setReportDocVersion] = useState(0);
  const stoneAttrEdits = useAtomValue(stoneAttrEditsAtom);
  useStoneReportSync(
    reportEditorRef,
    reportStones,
    stoneVerdicts,
    stoneAttrEdits,
    reportDocVersion,
  );
  // Save→advance for the stone worklist (resolves next study from ?collection=/?cohort=).
  const stoneNav = useStoneWorklistNav(studyUid);

  // Resolve a stone's representative slice → a bone-windowed, full-resolution
  // canvas for the StoneRail thumbnail (crop + calipers are applied downstream
  // in renderStoneThumbnail). Reads canonical metadata the same way
  // selectFinding does. Throws when the instance/CDN can't be resolved so the
  // rail logs + shows no thumbnail — never a silent blank canvas.
  const loadSavedPreviewGeometry = useCallback(
    async (seriesUid: string, sliceIndex: number) => {
      const instance = studyMetadata?.series?.[seriesUid]?.[sliceIndex];
      const cdnBase = studyMetadata?.canonical_cdn_base_url;
      if (!instance || !cdnBase) throw new Error("Saved preview frame is unavailable");
      const { geometry } = await loadS3PreviewFrame(cdnBase, instance);
      if (!geometry) throw new Error("Saved preview DICOM frame has no valid geometry");
      return geometry;
    },
    [studyMetadata],
  );

  const loadStonePreviewFrame = useCallback(
    async (
      seriesUid: string,
      sliceIndex: number,
      frameWindow: { windowWidth: number; windowCenter: number },
    ): Promise<HTMLCanvasElement> => {
      const instance = studyMetadata?.series?.[seriesUid]?.[sliceIndex];
      const cdnBase = studyMetadata?.canonical_cdn_base_url;
      if (!instance || !cdnBase) {
        throw new Error(
          `stone preview: unresolved instance/CDN (series=${seriesUid} slice=${sliceIndex})`,
        );
      }
      const { pixelData, rows, columns, rescale } = await loadS3PreviewFrame(cdnBase, instance);
      const canvas = document.createElement("canvas");
      canvas.width = columns;
      canvas.height = rows;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("stone preview: 2d canvas context unavailable");
      const img = ctx.createImageData(columns, rows);
      img.data.set(previewWindowPixels(pixelData, rows, columns, frameWindow, rescale));
      ctx.putImageData(img, 0, 0);
      return canvas;
    },
    [studyMetadata],
  );

  // Focus mode - fills entire viewer with single viewport (hides all sidebars)
  const focusMode = useAtomValue(focusModeAtom);

  const handleSliceChange = useCallback(
    (newSliceOrUpdater: number | ((prev: number) => number)) => {
      const currentSeries = loadedSeries.find(
        (s) => getBaseFilename(s.filename) === selectedSeriesId,
      );

      // Calculate the target slice number
      const targetSlice =
        typeof newSliceOrUpdater === "function"
          ? newSliceOrUpdater(currentSlice)
          : newSliceOrUpdater;

      // Check if the slice exists in the DICOM series
      const isSliceAvailable =
        currentSeries && "imageIds" in currentSeries
          ? targetSlice >= 0 && targetSlice < currentSeries.imageIds.length
          : false;

      // Only update if the slice is available
      if (isSliceAvailable) {
        setCurrentSlice(targetSlice);
      }
    },
    [loadedSeries, selectedSeriesId, currentSlice, setCurrentSlice],
  );

  const handleWindowPreset = useCallback(
    (preset: WindowPreset, center: number, width: number) => {
      // Local state drives the WindowPreset chip's "active" indicator (React-
      // side); the hanging-protocol all-slot windowing fan-out lives in the
      // setWindowPreset command.
      setWindowing({ preset, center, width });
      if (studyMetadata && hasHangingProtocolSupport(studyMetadata.series_metadata)) {
        runCommand("setWindowPreset", { center, width });
      }
    },
    [studyMetadata],
  );

  // Register keyboard shortcuts for presets and sidebar toggles
  const measureMode = useAtomValue(measureModeAtom);
  useViewerHotkeys({
    onPresetSelect: handleWindowPreset,
    onPrevious: segmentationAnnotationContext?.hasPrevious
      ? segmentationAnnotationContext.onPrevious
      : undefined,
    onNext: segmentationAnnotationContext?.hasNext
      ? segmentationAnnotationContext.onNext
      : undefined,
    onNextUnlabeled: segmentationAnnotationContext?.hasNextUnlabeled
      ? segmentationAnnotationContext.onNextUnlabeled
      : undefined,
    segmentationMode: segmentationStudyContext
      ? "study"
      : factorySegmentationContext || measureMode
        ? "legacy"
        : "disabled",
    // ENG-188: no engine switching in the reader study; the factory keeps it.
    segmentationEngineAvailable: !!factorySegmentationContext,
    onTogglePredictionMasks: predictionMaskControlAvailable ? togglePredictionMasks : undefined,
    enabled: true,
  });

  // Thin wrapper: gathers studyMetadata-dependent context (windowing default,
  // invert heuristic, transfer-syntax warning) which lives in React state,
  // then dispatches to the selectFinding command for the actual effect fan-out.
  // Also resolves the click into a focused-finding-index so Tab can cycle.
  // `locate: false` jumps without the locator ring (no in-plane location);
  // `window` sets the destination pane's window for the finding.
  const handleFindingSelectSimplified = useCallback(
    (
      condition: string,
      target: FindingJumpTarget | null,
      { locate = true, window }: { locate?: boolean; window?: WindowLevel } = {},
    ) => {
      const seriesId = target?.seriesId ?? null;
      const sliceIndex = target?.sliceIndex ?? null;
      const meta = seriesId ? studyMetadata?.series_metadata?.[seriesId] : null;
      const seriesInstances = seriesId ? studyMetadata?.series?.[seriesId] : undefined;
      if (seriesId && seriesInstances) {
        warnOnMixedSignedTransferSyntax(seriesId, seriesInstances);
      }
      const idx = orderedFindings.findIndex(
        (f) => f.seriesId === seriesId && f.sliceIndex === sliceIndex,
      );
      setFocusedFindingIndex(idx === -1 ? null : idx);
      runCommand("selectFinding", {
        finding: condition,
        target,
        locate,
        window,
        setSeriesOptions:
          meta || seriesInstances
            ? {
                windowing: meta ? getSeriesWindowing(meta) : undefined,
                isInverted: seriesInstances ? shouldAutoInvert(seriesInstances) : undefined,
              }
            : undefined,
      });
    },
    [
      studyMetadata?.series_metadata,
      studyMetadata?.series,
      orderedFindings,
      setFocusedFindingIndex,
    ],
  );

  // Resolve SOP Instance UID to (seriesId, sliceIndex) for key-slice clicks
  const keySliceTarget = useCallback(
    (topSliceUid: string): FindingJumpTarget | null => {
      for (const [seriesId, instances] of Object.entries(instanceSliceInfoMap)) {
        const info = instances[topSliceUid];
        if (info)
          return {
            seriesId,
            sliceIndex: info.index,
            frameOfReferenceUid:
              studyMetadata?.series_metadata?.[seriesId]?.frame_of_reference_uid ?? null,
          };
      }
      return null;
    },
    [instanceSliceInfoMap, studyMetadata?.series_metadata],
  );
  const handleComparisonFindingClick = useCallback(
    (topSliceUid: string) => {
      const target = keySliceTarget(topSliceUid);
      if (target) handleFindingSelectSimplified("", target);
    },
    [keySliceTarget, handleFindingSelectSimplified],
  );
  // A host rail's key slices carry no in-plane location, so its jumps draw no
  // locator ring: one would mark the slice origin, not the finding. The rail
  // names the window to show the finding in.
  const handleRailKeySliceJump = useCallback(
    (topSliceUid: string, window: WindowLevel) => {
      const target = keySliceTarget(topSliceUid);
      if (target) handleFindingSelectSimplified("", target, { locate: false, window });
    },
    [keySliceTarget, handleFindingSelectSimplified],
  );

  // Slice-state + instance-UID-mapping bridges moved into
  // useDicomSeriesLoader — the hook that owns series loading also owns the
  // bridges from its outputs to viewer-shared atoms.

  // Note: Series change reset handled by key prop on CaseViewer (see Worklist.tsx)
  // Component remounts when studyId changes, naturally resetting all state

  // Cleanup effect
  useEffect(() => {
    // Cleanup function that runs when component unmounts
    return () => {
      terminateAllWorkers();
      // Purge entire Cornerstone cache - this is the only reliable way to free memory
      // Selective removal by study ID doesn't work due to Cornerstone's internal cache structure
      purgeAllCornerstoneCache();
    };
  }, [terminateAllWorkers]);

  // Note: Spatial groups computation moved to useMemo for better performance
  // No longer need effect to update spatial groups - happens automatically

  // Handler for when report is signed - refetch report state to update signed status
  // Note: handleReportSigned removed - PrototypeReport is read-only demo mode
  // Note: PredictionsProvider removed - predictions managed in local state

  const handleExitFocusMode = useCallback(() => {
    runCommand("exitFocusMode", {});
  }, []);

  // Measure-mode banner state — shown above the viewport when 📐 is armed
  const measureLoading = useAtomValue(measureLoadingAtom);
  const measureError = useAtomValue(measureErrorAtom);
  const measureSession = useAtomValue(measureSessionAtom);
  const measureClickPin = useAtomValue(measureClickPinAtom);
  const slotSeriesSlices = useAtomValue(allSlotSeriesSlicesSelector);

  // True when at least one slot is currently displaying a slice the mask
  // covers (in the click's series). Drives pin visibility so the volume
  // label only appears on slices where the mask actually exists, but tracks
  // the full mask range — not just the click slice.
  const pinSliceVisible = !!(
    measureClickPin &&
    slotSeriesSlices.some((s) => {
      if (s.seriesId !== measureClickPin.seriesUid) return false;
      const indices = measureClickPin.maskSliceIndices;
      if (indices && indices.length > 0) return indices.includes(s.sliceIndex);
      return s.sliceIndex === measureClickPin.sliceIndex;
    })
  );

  // Pre-click cursor follower: while measure mode is armed and no click pin
  // exists yet, render a purple ring at the mouse position when it's over a
  // viewport. Cleared when the user clicks (pin takes over) or cancels.
  const [measureCursorPos, setMeasureCursorPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    if (!measureMode || measureClickPin) {
      setMeasureCursorPos(null);
      return;
    }
    const onMove = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const overViewport = !!target?.closest('[data-measure-target="true"]');
      setMeasureCursorPos(overViewport ? { x: e.clientX, y: e.clientY } : null);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [measureMode, measureClickPin]);

  // DRY w/ labeling: while measure mode is active, flip the same global
  // segmentation-active flag and active-segment-index that the labeling
  // panel uses (SegmentationAnnotationPanel.startCategoryAnnotation). That puts
  // the case viewer into the same render state — segmentation registered,
  // contour representation visible, segment 1 marked active and visible —
  // so the contour overlay shows up exactly the way labeling's
  // RegionGrowTool contour does.
  // Cornerstone contour-style override while in measure mode. State-sync
  // (isSegmentationActive, activeSegmentIndex) lives in enter/exitMeasureMode
  // commands; this effect only synchronizes with Cornerstone's external style
  // API, which is a legit use of useEffect.
  useEffect(() => {
    if (!measureMode) return;
    let restoreStyle: (() => void) | null = null;
    try {
      const csTools = getCornerstoneTools();
      const contourType = csTools.Enums?.SegmentationRepresentations?.Contour;
      const segStyle = csTools.segmentation?.config?.style;
      if (contourType && segStyle?.setStyle && segStyle.getStyle) {
        const prev = segStyle.getStyle({ type: contourType }) ?? {};
        segStyle.setStyle(
          { type: contourType },
          {
            outlineWidth: 1,
            outlineOpacity: 1,
            renderOutline: true,
            renderFill: true,
            fillAlpha: 0.35,
            activeSegmentOutlineWidthDelta: 1,
          },
        );
        restoreStyle = () => {
          try {
            segStyle.setStyle({ type: contourType }, prev, false);
          } catch {
            /* style API may not be available — best effort */
          }
        };
      }
    } catch {
      /* cornerstone not initialized */
    }
    return () => {
      restoreStyle?.();
    };
  }, [measureMode]);

  return (
    <div
      className="flex flex-col h-full bg-bg-2 text-white overflow-hidden"
      onMouseEnter={() => setHoveredSurface("viewer")}
      onMouseLeave={() => setHoveredSurface((prev) => (prev === "viewer" ? null : prev))}
    >
      <GpuVerdictBanner />
      {/* Headless: loads the cherry-picked rdn60 stone mask overlay across the
          MPR panes when an allowlisted study opens. No-op for other studies. */}
      <StoneOverlayLoader studyId={showSegmentationStudyPredictions ? studyData.studyId : null} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Pre-click cursor follower — purple ring tracking the mouse while
          measure mode is armed and the user hasn't clicked yet. Hidden when
          the cursor is outside any viewport. */}
        {measureCursorPos && (
          <div
            className="pointer-events-none fixed z-60"
            style={{
              left: `${measureCursorPos.x}px`,
              top: `${measureCursorPos.y}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div
              className="rounded-full border-2 border-signal-stroke shadow-lg shadow-signal/50"
              style={{
                width: "20px",
                height: "20px",
                background: "rgba(67, 97, 238, 0.25)",
              }}
            />
          </div>
        )}
        {/* Floating click pin — page-level fixed position. While loading, a
          purple ring pulses at the click site. Once the volume resolves the
          ring is replaced by a labeled callout, and the callout only renders
          on slices that contain the mask (matched via series + slice index). */}
        {measureClickPin && (measureClickPin.volumeMl == null || pinSliceVisible) && (
          <div
            className="pointer-events-none fixed z-60"
            style={{
              left: `${measureClickPin.pageX}px`,
              top: `${measureClickPin.pageY}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            {measureClickPin.volumeMl == null ? (
              <div
                className="rounded-full border-2 border-signal-stroke shadow-lg shadow-signal/50"
                style={{
                  width: "20px",
                  height: "20px",
                  background: "rgba(67, 97, 238, 0.5)",
                  animation: "pulse 1.2s ease-in-out infinite",
                }}
              />
            ) : (
              <div
                className="absolute whitespace-nowrap rounded bg-signal px-2 py-0.5 text-xs font-semibold text-white shadow-lg"
                style={{ left: "16px", top: "-8px" }}
              >
                {measureClickPin.volumeMl.toFixed(2)} mL
              </div>
            )}
          </div>
        )}
        <div className="flex-1 flex">
          {/* Left sidebar - Collapsible via ',' key, hidden in focus mode */}
          <div className={focusMode.enabled ? "hidden" : ""}>
            <LeftSidebar
              studyMetadata={studyMetadata}
              studyId={studyData.studyId}
              activeWindowPreset={windowing.preset}
              onWindowPresetSelect={handleWindowPreset}
              onSeriesSelect={handleSeriesSelect}
              isCollapsed={leftSidebarCollapsed}
              onToggleCollapse={() => setLeftSidebarCollapsed((prev) => !prev)}
            />
          </div>

          {/* Main content area */}
          <div className="flex-1 flex">
            {/* Center canvas - fills available space without padding */}
            <div className="flex-1 relative bg-black">
              {/* 📐 Click-to-measure banner — surfaces when measureModeAtom is set */}
              {measureMode && (
                <div className="absolute top-0 left-0 right-0 z-50 bg-signal/90 text-white px-4 py-2 flex items-center justify-between shadow-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📐</span>
                    <span className="text-sm font-medium">
                      {measureLoading
                        ? "Measuring segmentation..."
                        : measureError
                          ? `Measurement error: ${measureError}`
                          : measureSession
                            ? `Refine: shift+click = add region, ⌘/ctrl+click = remove region. Done when accurate.`
                            : `Click on the ${measureMode.baseName.toLowerCase()} in the viewer to measure it`}
                    </span>
                  </div>
                  <button
                    onClick={() => runCommand("exitMeasureMode", {})}
                    className="text-xs text-white/80 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                  >
                    {measureSession ? "Done" : "Cancel"}
                  </button>
                </div>
              )}
              {/* Non-canonicalized studies don't have DICOM bytes to load.
                fetchStudyMetadata throws StudyNotCanonicalizedError on 423;
                useDicomSeriesLoader surfaces that as `studyNotCanonicalized`.
                Show the same friendly panel the labeling viewer uses (#305)
                instead of parking the user on the spinner indefinitely. */}
              {studyNotCanonicalized ? (
                <StudyNotCanonicalizedPanel />
              ) : imagesError ? (
                <p role="alert" className="p-6 text-sm text-fg-muted">
                  Could not load the images: {imagesError}
                </p>
              ) : loadingStage !== "ready" ? (
                <LoadingOverlay stage={loadingStage} metadata={studyMetadata} />
              ) : studyMetadata &&
                hasHangingProtocolSupport(studyMetadata.series_metadata) &&
                reportIsLoading &&
                !resolvedPrioritySeriesId ? (
                <LoadingOverlay stage="loading-frames" metadata={studyMetadata} />
              ) : studyMetadata &&
                hasHangingProtocolSupport(studyMetadata.series_metadata) &&
                !resolvedPrioritySeriesId &&
                !rightRail ? (
                // No model prediction and user hasn't picked — show series picker.
                // A host rail instead opens the hanging protocol with no priority
                // series: its own axial-first assignment, every pane at slice 0.
                <SeriesPicker
                  studyMetadata={studyMetadata}
                  onSeriesSelect={setUserSelectedSeriesId}
                  title="Select primary series"
                />
              ) : studyMetadata && hasHangingProtocolSupport(studyMetadata.series_metadata) ? (
                // Use 2x2 hanging protocol grid with priority series
                <HangingProtocolGrid
                  studyId={studyData.studyId}
                  studyMetadata={studyMetadata}
                  prioritySeriesId={resolvedPrioritySeriesId}
                  {...caseViewerGridLayout({
                    enableSegmentation,
                    segmentationLayout,
                    hasAnnotationContext: !!segmentationAnnotationContext,
                  })}
                />
              ) : selectedSeriesId && studyMetadata ? (
                // Single-viewport fallback when no hanging protocol matches.
                // Route to StackViewport for 2D / non-reconstructable series
                // (scouts, single-frame SCs, X-rays) and VolumeViewport for
                // reconstructable 3D volumes — same predicate as ProtocolViewport.
                // Props are identical for both routes (the divergent
                // volume-only props — useGlobalSliceState, startAtMiddleSlice,
                // coordinatedResize, onCameraChange — only matter inside the
                // hanging-protocol layout, not in this single-viewport
                // fallback), so spread a shared object instead of duplicating.
                (() => {
                  const seriesMeta = studyMetadata.series_metadata?.[selectedSeriesId];
                  const ViewportComponent =
                    decideViewport(seriesMeta, gpuVerdict).kind === "stack"
                      ? StackViewport
                      : VolumeViewport;
                  return (
                    <>
                      <ViewportComponent
                        seriesId={selectedSeriesId}
                        studyMetadata={studyMetadata}
                        studyId={studyData.studyId}
                        windowCenter={windowing.center}
                        windowWidth={windowing.width}
                        onSliceChange={(slice) => handleSliceChange(slice)}
                        onWindowChange={(center, width) =>
                          setWindowing((prev) => ({ ...prev, center, width }))
                        }
                      />
                      {/* No ViewportOverlay in this branch, so anchor the
                          badge directly. */}
                      <div className="absolute bottom-2 left-2 z-40 pointer-events-none">
                        <ViewportModeBadge meta={seriesMeta} />
                      </div>
                    </>
                  );
                })()
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-bg-2 text-fg-muted">
                  <p>Select a series to view</p>
                </div>
              )}

              {/* Focus mode overlay controls */}
              {focusMode.enabled && (
                <>
                  {/* Exit button */}
                  <button
                    onClick={handleExitFocusMode}
                    className="absolute top-4 right-4 z-50 p-2 rounded-full bg-surface-2/80 hover:bg-surface text-white transition-colors"
                    title="Exit focus mode (Esc)"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>

                  {/* Bottom controls */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-bg-2/90 px-4 py-2 rounded-lg">
                    {/* Window presets */}
                    <div className="flex gap-1">
                      {(["soft_tissue", "lung", "bone", "brain", "liver"] as const).map(
                        (preset) => (
                          <button
                            key={preset}
                            onClick={() => {
                              const presets = {
                                soft_tissue: [40, 400],
                                lung: [-600, 1500],
                                bone: [400, 1800],
                                brain: [40, 80],
                                liver: [60, 150],
                              };
                              const [center, width] = presets[preset];
                              handleWindowPreset(preset, center, width);
                            }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              windowing.preset === preset
                                ? "bg-primary text-white"
                                : "bg-surface text-fg-muted hover:bg-surface-2"
                            }`}
                            title={preset.replace("_", " ")}
                          >
                            {preset === "soft_tissue" ? "ST" : preset.charAt(0).toUpperCase()}
                          </button>
                        ),
                      )}
                    </div>
                    {/* Slice indicator */}
                    <div className="text-sm text-fg-muted">
                      {currentSlice + 1} / {totalSlices}
                    </div>
                    {/* Hint */}
                    <div className="text-xs text-fg-muted">Esc to exit</div>
                  </div>
                </>
              )}
            </div>

            {/* Right sidebar - Report panel, collapsible via '.' key, hidden in focus mode */}
            <div className={focusMode.enabled ? "hidden" : ""}>
              {rightSidebarCollapsed && isTriageResult ? (
                <TriageCollapsedRail
                  count={triageRailCount(modelPrediction?.triage_review, !triageNotRouted)}
                  onExpand={() => setRightSidebarCollapsed(false)}
                />
              ) : rightSidebarCollapsed ? (
                <CollapsedSidebarStrip
                  side="right"
                  onExpand={() => setRightSidebarCollapsed(false)}
                  shortcutHint="."
                />
              ) : (
                <div
                  className="shrink-0 h-full overflow-hidden flex flex-col relative"
                  style={{ width: rightSidebarWidth }}
                >
                  {/* Drag handle to resize the report pane (left edge) */}
                  <div
                    onPointerDown={handleRightSidebarResize}
                    className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-40"
                    title="Drag to resize"
                    role="separator"
                    aria-orientation="vertical"
                  />
                  {/* Tab content */}
                  {/*
                    Panel precedence is intentional: a surfacer cohort wins over
                    the reviewer panel, so a reviewer opening a surfacer cohort
                    sees SurfacerPanel (the cohort's mode is the stronger signal
                    than the caller's role).
                  */}
                  {/* Findings ⇄ Report tab strip. The finding rail renders its own
                      header, so this is suppressed there to avoid a doubled strip. */}
                  {!disableReportQueries &&
                    !isNeuroResult &&
                    !isTriageResult &&
                    !modelIdentityMissing &&
                    sidebarFindingCount > 0 &&
                    rightSidebarTab !== "findings" && (
                      <div className="flex shrink-0 border-b border-border">
                        <button
                          type="button"
                          onClick={() => setRightSidebarTab("findings")}
                          className="font-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2 text-fg-muted hover:text-fg transition-colors"
                        >
                          {sidebarFindingLabel} · {sidebarFindingCount}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRightSidebarTab("report")}
                          className={`relative font-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2 transition-colors ${
                            rightSidebarTab === "report" ? "text-fg" : "text-fg-muted hover:text-fg"
                          }`}
                        >
                          Report
                          {rightSidebarTab === "report" && (
                            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-signal" />
                          )}
                        </button>
                      </div>
                    )}
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {rightRail ? (
                      rightRail(handleRailKeySliceJump)
                    ) : triageNotRouted ? (
                      <TriageNotRoutedPanel
                        device={triageRouting.device}
                        reason={triageRouting.reason}
                        onCollapse={() => setRightSidebarCollapsed(true)}
                      />
                    ) : modelIdentityMissing ? (
                      <p role="status" className="p-4 text-sm text-fg-muted">
                        {predictionError
                          ? "Could not load model results"
                          : predictionLoading
                            ? "Loading model results"
                            : "Model results unavailable"}
                      </p>
                    ) : isNeuroResult ? (
                      <NeuroReviewPanel
                        result={modelPrediction?.neuro_review}
                        findingTargets={neuroTargets}
                        onFindingSelect={(_, target) => {
                          // Image jumps repeat; a named report finding would toggle selection.
                          handleFindingSelectSimplified("", target);
                        }}
                        loading={predictionLoading}
                        error={predictionError}
                      />
                    ) : isTriageResult ? (
                      <TriageNotificationPanel
                        result={modelPrediction?.triage_review}
                        loading={predictionLoading}
                        error={predictionError}
                        studyId={studyData.studyId}
                        ageRecorded={triageRouting?.ageRecorded ?? true}
                        onCollapse={() => setRightSidebarCollapsed(true)}
                      />
                    ) : surfacerMode && surfacerData ? (
                      <div className="h-full flex flex-col">
                        <div
                          className="min-h-0 overflow-hidden"
                          style={{
                            flexBasis: `${surfacerSplit * 100}%`,
                            flexGrow: 0,
                            flexShrink: 0,
                          }}
                        >
                          <SurfacerPanel
                            key={surfacerData.study_uid}
                            data={surfacerData}
                            onFindingClick={handleComparisonFindingClick}
                            onSelectExcerpt={setHighlightExcerpt}
                          />
                        </div>
                        <div
                          onPointerDown={handleSurfacerSplitResize}
                          className="h-1 shrink-0 cursor-row-resize bg-surface/60 hover:bg-primary/40 active:bg-primary/60 transition-colors"
                          role="separator"
                          aria-orientation="horizontal"
                          title="Drag to resize"
                        />
                        <div className="flex-1 min-h-0 overflow-hidden">
                          <OriginalReportPanel
                            reportText={surfacerData.original_report_text}
                            highlightExcerpt={highlightExcerpt}
                          />
                        </div>
                      </div>
                    ) : showReviewerPanel ? (
                      <ReviewerReportShell
                        reviewCohortId={reviewCohortId}
                        studyUid={studyUid}
                        reportStudyId={studyData.studyId}
                        predictions={modelPrediction?.base_model_prediction ?? {}}
                        onFindingClick={handleComparisonFindingClick}
                        onInlineFindingClick={(target) => handleFindingSelectSimplified("", target)}
                      />
                    ) : rightSidebarTab === "comparison" && reporterComparison ? (
                      <ComparisonPanel
                        data={reporterComparison}
                        onFindingClick={handleComparisonFindingClick}
                      />
                    ) : rightSidebarTab === "findings" &&
                      segmentationSidebarMode === "standard-findings" ? (
                      <StoneRail
                        studyUid={studyData.studyId}
                        onStoneSelect={(stone) => {
                          // finding: null — skip selectFinding's toggle-off path
                          // (selectFinding.ts:30) so a repeat click re-jumps
                          // instead of deselecting; stone.id matches no report
                          // finding condition anyway.
                          runCommand("selectFinding", {
                            finding: null,
                            target: {
                              seriesId: stone.seriesInstanceUid,
                              sliceIndex: stone.sliceIndex,
                              frameOfReferenceUid: stone.frameOfReferenceUid,
                              worldCentroid: stone.worldCentroid,
                              segmentIndex: stone.segmentIndex,
                              pixelCentroid: stone.pixelCentroid,
                            },
                          });
                        }}
                        onShowReportTab={() => setRightSidebarTab("report")}
                        loadFrame={loadStonePreviewFrame}
                        onAdvance={stoneNav.goToNext}
                        hasNext={stoneNav.hasNext}
                      />
                    ) : rightSidebarTab === "findings" &&
                      segmentationSidebarMode === "prediction-readonly" ? (
                      <div className="h-full min-h-0 overflow-y-auto bg-bg-2">
                        <SegmentationPreviewRail
                          previews={segmentationPreviews}
                          masksVisible={predictionMasksVisible}
                          onToggleMasks={
                            predictionMaskControlAvailable ? togglePredictionMasks : undefined
                          }
                          onPreviewSelect={(preview) => {
                            runCommand("selectFinding", {
                              finding: null,
                              target: {
                                seriesId: preview.seriesInstanceUid,
                                sliceIndex: preview.sliceIndex,
                                frameOfReferenceUid: preview.frameOfReferenceUid,
                                worldCentroid: preview.worldCentroid,
                                pixelCentroid: preview.pixelCentroid,
                                segmentIndex: preview.segmentIndex,
                              },
                            });
                          }}
                          onShowReportTab={() => setRightSidebarTab("report")}
                          showReportTab={!disableReportQueries}
                          verdicts={stoneVerdicts}
                          onVerdictChange={(previewId, verdict) =>
                            setStoneVerdicts((current) => ({ ...current, [previewId]: verdict }))
                          }
                          loadFrame={loadStonePreviewFrame}
                        />
                      </div>
                    ) : rightSidebarTab === "findings" &&
                      segmentationSidebarMode === "segmentation-study" ? (
                      <div className="h-full min-h-0 flex flex-col bg-bg text-fg select-none">
                        {segmentationAnnotationContext && (
                          <div className="px-3 pt-3 pb-2 border-b border-border-strong shrink-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
                                {segmentationStudyContext
                                  ? `Segmentation · ${
                                      segmentationStudyContext.templateMode === "blank"
                                        ? "Blank"
                                        : "AI Output"
                                    }`
                                  : "3D Segmentation Annotation"}
                              </span>
                              <div className="flex items-center gap-2">
                                {segmentationStudyContext && (
                                  <SegmentationCaseTimer
                                    projectId={projectId}
                                    studyUid={studyData.studyId}
                                  />
                                )}
                                <WorkListButton
                                  onClick={segmentationAnnotationContext.onWorklist}
                                  compact
                                />
                                <span className="text-xs text-fg-muted">
                                  {segmentationAnnotationContext.currentIndex + 1} /{" "}
                                  {segmentationAnnotationContext.totalCases}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-fg-muted truncate">
                              {segmentationAnnotationContext.caseLabel}
                              {segmentationCardCount > 0
                                ? ` · ${segmentationCardCount} segmentations`
                                : " · no AI output"}
                            </div>
                          </div>
                        )}
                        <div className="flex-1 min-h-0 overflow-y-auto">
                          <SegmentationPreviewRail
                            statusContent={
                              segmentationAnnotationContext && (
                                <CandidateDecisionStatus
                                  decisions={candidateVerdicts}
                                  annotations={factoryAnnotationsQuery}
                                  manualSaving={updateSavedAnnotation.isPending}
                                  hasUnlinkedAnnotations={!!hasUnlinkedAiAnnotations}
                                  imagesLoading={imagesLoading}
                                  imagesLoadUnknown={imagesLoadUnknown}
                                />
                              )
                            }
                            previews={segmentationPreviews}
                            savedAnnotations={additionalSavedAnnotations}
                            predictionAnnotations={predictionCardAnnotations}
                            onEditSavedAnnotation={(annotation, preview) => {
                              if (showAnnotationEditor || caseLocked || imagesLoading) return;
                              setEditingSegmentationPreviewId(null);
                              if (preview)
                                runCommand("selectFinding", {
                                  finding: null,
                                  target: {
                                    seriesId: preview.seriesInstanceUid,
                                    sliceIndex: preview.sliceIndex,
                                    frameOfReferenceUid: preview.frameOfReferenceUid,
                                    worldCentroid: preview.worldCentroid,
                                    pixelCentroid: preview.pixelCentroid,
                                  },
                                });
                              setSavedEditRequest({
                                annotationId: annotation.annotation_id,
                                requestId: Date.now(),
                              });
                            }}
                            onReviewSavedAnnotation={async (annotation, review) => {
                              if (caseLocked) return;
                              const attributes = { ...annotation.attributes };
                              if (review === "pending") delete attributes.manual_annotation_review;
                              else attributes.manual_annotation_review = review;
                              await updateSavedAnnotation.mutateAsync({
                                annotationId: annotation.annotation_id,
                                update: { attributes },
                              });
                            }}
                            loadFrameGeometry={loadSavedPreviewGeometry}
                            resampleSavedVolume={!!segmentationAnnotationContext}
                            studyMetadata={studyMetadata}
                            conditionLabels={conditionLabels}
                            onSavedPreviewSelect={(preview) => {
                              runCommand("selectFinding", {
                                finding: null,
                                target: {
                                  seriesId: preview.seriesInstanceUid,
                                  sliceIndex: preview.sliceIndex,
                                  frameOfReferenceUid: preview.frameOfReferenceUid,
                                  worldCentroid: preview.worldCentroid,
                                  pixelCentroid: preview.pixelCentroid,
                                },
                              });
                            }}
                            masksVisible={predictionMasksVisible}
                            onToggleMasks={
                              predictionMaskControlAvailable ? togglePredictionMasks : undefined
                            }
                            onPreviewSelect={(preview) => {
                              runCommand("selectFinding", {
                                finding: null,
                                target: {
                                  seriesId: preview.seriesInstanceUid,
                                  sliceIndex: preview.sliceIndex,
                                  frameOfReferenceUid: preview.frameOfReferenceUid,
                                  worldCentroid: preview.worldCentroid,
                                  pixelCentroid: preview.pixelCentroid,
                                  segmentIndex: preview.segmentIndex,
                                },
                              });
                            }}
                            onShowReportTab={() => undefined}
                            showReportTab={false}
                            onAcceptPreview={
                              segmentationAnnotationContext ? acceptPrediction : undefined
                            }
                            requireReaderConfidence={isSegmentationStudy}
                            onSaveConfidence={
                              segmentationAnnotationContext ? saveReaderConfidence : undefined
                            }
                            onDeclinePreview={
                              segmentationAnnotationContext ? declinePrediction : undefined
                            }
                            onUndoPreview={
                              segmentationAnnotationContext ? undoPredictionVerdict : undefined
                            }
                            verdicts={stoneVerdicts}
                            verdictsDisabled={
                              caseLocked ||
                              (!!segmentationAnnotationContext && !candidateVerdicts.ready) ||
                              imagesLoading
                            }
                            lockedPreviewIds={
                              segmentationAnnotationContext ? lockedPreviewIds : undefined
                            }
                            maskLoadingPreviewIds={
                              segmentationAnnotationContext ? maskLoadingPreviewIds : undefined
                            }
                            maskFailedPreviewIds={
                              segmentationAnnotationContext ? maskFailedPreviewIds : undefined
                            }
                            maskUnavailablePreviewIds={
                              segmentationAnnotationContext ? maskUnavailablePreviewIds : undefined
                            }
                            maskLoadingDeadlines={
                              segmentationAnnotationContext ? maskLoadingDeadlines : undefined
                            }
                            imagesLoading={imagesLoading}
                            editDisabled={editBlockedBySavedAnnotations}
                            editHiddenReasons={editHiddenReasons}
                            onVerdictChange={
                              segmentationAnnotationContext
                                ? undefined
                                : (previewId, verdict) =>
                                    setStoneVerdicts((current) => ({
                                      ...current,
                                      [previewId]: verdict,
                                    }))
                            }
                            onEditPreview={(preview) => {
                              // The rail disables Edit on editHiddenReasons too.
                              if (
                                showAnnotationEditor ||
                                caseLocked ||
                                editHiddenReasons[preview.id]
                              )
                                return;
                              setSavedEditRequest(null);
                              if (segmentationAnnotationContext) {
                                // The rail disables Edit on this same condition.
                                if (
                                  candidateVerdicts.isCandidateLocked(
                                    preview.condition,
                                    preview.sourceSegmentIndex,
                                  ) ||
                                  editBlockedBySavedAnnotations ||
                                  imagesLoading ||
                                  maskLoadingPreviewIds.has(preview.id) ||
                                  maskFailedPreviewIds.has(preview.id) ||
                                  maskUnavailablePreviewIds.has(preview.id)
                                )
                                  return;

                                const candidateKey = candidateVerdictKey(
                                  preview.condition,
                                  preview.sourceSegmentIndex,
                                );
                                const hasSavedLink = Object.hasOwn(
                                  acceptedPredictionAnnotationIds,
                                  candidateKey,
                                );
                                const savedAnnotation =
                                  savedPredictionAnnotations.get(candidateKey);

                                if (hasSavedLink && !savedAnnotation) {
                                  emit("toast", {
                                    message:
                                      "The saved annotation could not be found. Please reload the case.",
                                  });
                                  return;
                                }
                              }
                              if (
                                segmentationAnnotationContext &&
                                !startCandidateEdit(preview.condition, preview.sourceSegmentIndex)
                              )
                                return;
                              setEditingSegmentationPreviewId(preview.id);
                              setSegmentationAnnotationMode("edit");
                              setShowAnnotationEditor(true);
                              runCommand("selectFinding", {
                                finding: null,
                                target: {
                                  seriesId: preview.seriesInstanceUid,
                                  sliceIndex: preview.sliceIndex,
                                  frameOfReferenceUid: preview.frameOfReferenceUid,
                                  worldCentroid: preview.worldCentroid,
                                  pixelCentroid: preview.pixelCentroid,
                                  segmentIndex: preview.segmentIndex,
                                },
                              });
                            }}
                            annotationContent={
                              <SegmentationAnnotationPanel
                                projectId={projectId}
                                caseId={factorySegmentationContext?.caseId ?? studyData.studyId}
                                studyInstanceUid={studyData.studyId}
                                seriesInstanceUid={selectedSeriesId ?? undefined}
                                savedEditRequest={savedEditRequest}
                                onNext={() => {}}
                                onPrevious={() => {}}
                                hasNext={false}
                                hasPrevious={false}
                                currentIndex={0}
                                totalCases={1}
                                modelInitialized={editingSegmentationPreview !== null}
                                onBeforePredictionEdit={beginPredictionEdit}
                                getPredictionEditCompletion={getPredictionEditCompletion}
                                predictionEditSegmentIndex={
                                  editingSegmentationPreview?.segmentIndex ?? null
                                }
                                predictionEditAnnotationId={
                                  editingSegmentationPreview
                                    ? (acceptedPredictionAnnotationIds[
                                        candidateVerdictKey(
                                          editingSegmentationPreview.condition,
                                          editingSegmentationPreview.sourceSegmentIndex,
                                        )
                                      ] ?? null)
                                    : null
                                }
                                predictionEditSeriesInstanceUid={
                                  editingSegmentationPreview?.seriesInstanceUid ?? null
                                }
                                predictionEditCategory={
                                  editingSegmentationPreview
                                    ? persistedSegmentationCategory(
                                        editingSegmentationPreview.condition,
                                      )
                                    : null
                                }
                                hideLightMode
                                compactStudyMode
                                studyToolPolicy={!!segmentationStudyContext}
                                lockReason={segmentationStudyContext?.lockReason ?? null}
                                allowAiSegmentation={!!factorySegmentationContext}
                                loadSavedMasksByAnnotationId={!!segmentationAnnotationContext}
                                predictionSegmentIndices={predictionSegmentIndices}
                                saveCandidateAnnotation={
                                  segmentationAnnotationContext
                                    ? saveCandidateAnnotation
                                    : undefined
                                }
                                onCandidateEditStart={
                                  segmentationAnnotationContext ? startCandidateEdit : undefined
                                }
                                predictionEditActionsRef={predictionEditActions}
                                requireReaderConfidence={isSegmentationStudy}
                                predictionEditReaderConfidence={
                                  editingSegmentationPreview
                                    ? parseReaderConfidence(
                                        savedPredictionAnnotations.get(
                                          candidateVerdictKey(
                                            editingSegmentationPreview.condition,
                                            editingSegmentationPreview.sourceSegmentIndex,
                                          ),
                                        )?.attributes?.[READER_CONFIDENCE_KEY],
                                      )
                                    : null
                                }
                                annotationMetadata={
                                  segmentationAnnotationContext && editingSegmentationPreview
                                    ? {
                                        ai_prediction_condition:
                                          editingSegmentationPreview.condition,
                                        ai_prediction_source_segment_index:
                                          editingSegmentationPreview.sourceSegmentIndex,
                                      }
                                    : undefined
                                }
                                onUnchangedPredictionSave={
                                  segmentationAnnotationContext && editingSegmentationPreview
                                    ? saveUnchangedPredictionEdit
                                    : undefined
                                }
                                labelsToAssign={
                                  segmentationAnnotationContext ? annotationCategories : undefined
                                }
                                conditionLabels={
                                  segmentationAnnotationContext ? conditionLabels : undefined
                                }
                                onStartAnnotation={(mode = "new") => {
                                  if (mode === "new") setSavedEditRequest(null);
                                  setSegmentationAnnotationMode(mode);
                                  setShowAnnotationEditor(true);
                                }}
                                onCancelAnnotation={() => {
                                  setSavedEditRequest(null);
                                  setEditingSegmentationPreviewId(null);
                                  setSegmentationAnnotationMode("idle");
                                  setShowAnnotationEditor(false);
                                }}
                                onAnnotationSaved={() => {
                                  setSavedEditRequest(null);
                                  setSegmentationAnnotationMode("idle");
                                  setEditingSegmentationPreviewId(null);
                                  setShowAnnotationEditor(false);
                                }}
                              />
                            }
                            annotationActive={showAnnotationEditor}
                            editingPreviewId={editingSegmentationPreviewId}
                            unconfirmedPreviewId={unconfirmedPreviewId}
                            showHeading={false}
                            annotationPlaceholder={{
                              active: showAnnotationEditor && segmentationAnnotationMode === "new",
                            }}
                            lockReason={segmentationStudyContext?.lockReason ?? null}
                            loadFrame={loadStonePreviewFrame}
                          />
                        </div>
                        {segmentationStudyContext && (
                          <SegmentationStudyFooter
                            navigation={segmentationStudyContext}
                            annotationActive={showAnnotationEditor}
                            confirmComplete
                          />
                        )}
                        {factorySegmentationContext && (
                          <SegmentationStudyFooter
                            navigation={factorySegmentationContext}
                            annotationActive={showAnnotationEditor}
                          />
                        )}
                      </div>
                    ) : rightSidebarTab === "report" ||
                      !enableSegmentation ||
                      !segmentationAnnotationContext ? (
                      <PrototypeReport
                        studyId={studyData.studyId}
                        sessionMode="demo"
                        templateMode="ai"
                        onFindingClick={(target) => handleFindingSelectSimplified("", target)}
                        onEditorReady={setReportEditor}
                        onReportContentApplied={() => setReportDocVersion((v) => v + 1)}
                      />
                    ) : (
                      <SegmentationAnnotationPanel
                        studyToolPolicy={!!segmentationStudyContext}
                        projectId={projectId}
                        caseId={studyData.studyId}
                        studyInstanceUid={studyData.studyId}
                        seriesInstanceUid={selectedSeriesId ?? undefined}
                        onNext={() => {}}
                        onPrevious={() => {}}
                        hasNext={false}
                        hasPrevious={false}
                        currentIndex={0}
                        totalCases={1}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseViewer;
